import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { db } from '../config/firebaseConfig';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';

const PAGE_SIZE = 10;

function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function Notifications() {
  const intl = useIntl();
  const { user } = useContext(AuthContext);

  // Data/state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search / filters
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState(''); // '', unread, read
  const [typeFilter, setTypeFilter] = useState(''); // '', system, alert, warning, info
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Paging
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([null]);
  const [isLastPage, setIsLastPage] = useState(false);

  // Counters
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  // Version bump after mutations
  const [dataVersion, setDataVersion] = useState(0);

  // Dynamic types from visible data (fallback options)
  const typesFromData = useMemo(() => {
    const s = new Set();
    items.forEach((n) => n?.type && s.add(String(n.type)));
    return Array.from(s).sort();
  }, [items]);

  // Reset paging on filter/search change
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors([null]);
    setIsLastPage(false);
  }, [debouncedSearch, statusFilter, typeFilter, dateFrom, dateTo]);

  // Counters (no filters)
  useEffect(() => {
    if (!user?.uid) return;

    const col = collection(db, `users/${user.uid}/notifications`);
    (async () => {
      try {
        const totalSnap = await getCountFromServer(col);
        setTotalCount(totalSnap.data().count);

        const unreadQ = query(col, where('read', '==', false));
        const unreadSnap = await getCountFromServer(unreadQ);
        setUnreadCount(unreadSnap.data().count);
      } catch (err) {
        console.error('Error fetching notifications counters:', err);
      }
    })();
  }, [user, dataVersion]);

  // Fetch data
  const fetchNotifications = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const col = collection(db, `users/${user.uid}/notifications`);
      let q;

      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        q = query(
          col,
          where('searchable_terms', 'array-contains', s),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
      } else {
        const constraints = [];
        if (statusFilter === 'unread') constraints.push(where('read', '==', false));
        if (statusFilter === 'read') constraints.push(where('read', '==', true));
        if (typeFilter) constraints.push(where('type', '==', typeFilter));

        if (dateFrom) constraints.push(where('createdAt', '>=', new Date(`${dateFrom}T00:00:00`)));
        if (dateTo) constraints.push(where('createdAt', '<=', new Date(`${dateTo}T23:59:59`)));

        constraints.push(orderBy('createdAt', 'desc'));
        const cursor = pageCursors[currentPage - 1];
        if (cursor) constraints.push(startAfter(cursor));
        constraints.push(limit(PAGE_SIZE));

        q = query(col, ...constraints);
      }

      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(docs);

      if (!debouncedSearch) {
        setIsLastPage(snap.docs.length < PAGE_SIZE);
        const last = snap.docs[snap.docs.length - 1];
        if (last && pageCursors.length === currentPage) {
          setPageCursors((prev) => [...prev, last]);
        }
      } else {
        setIsLastPage(true);
      }
    } catch (err) {
      console.error('Erro ao buscar notificações:', err);
      setError(
        intl.formatMessage({
          id: 'notifications.errorLoading',
          defaultMessage: 'Erro ao carregar notificações. Verifique o console para mais detalhes.',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [user, debouncedSearch, statusFilter, typeFilter, dateFrom, dateTo, currentPage, pageCursors, intl]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, dataVersion]);

  // Paging handlers
  const handleNextPage = () => {
    if (!isLastPage && !loading) setCurrentPage((p) => p + 1);
  };
  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) setCurrentPage((p) => p - 1);
  };

  // Actions
  const markRead = async (id, readState) => {
    if (!user?.uid || !id) return;
    try {
      await updateDoc(doc(db, `users/${user.uid}/notifications`, id), {
        read: !!readState,
        updatedAt: serverTimestamp(),
      });
      setDataVersion((v) => v + 1);
    } catch (err) {
      console.error('Erro ao atualizar notificação:', err);
      toast.error(
        intl.formatMessage({
          id: 'notifications.updateError',
          defaultMessage: 'Erro ao atualizar notificação.',
        })
      );
    }
  };

  const deleteNotification = async (id) => {
    if (!user?.uid || !id) return;
    if (!confirm(intl.formatMessage({ id: 'notifications.confirmDelete', defaultMessage: 'Excluir esta notificação?' }))) return;
    try {
      await deleteDoc(doc(db, `users/${user.uid}/notifications`, id));
      toast.success(intl.formatMessage({ id: 'notifications.deleted', defaultMessage: 'Notificação excluída.' }));
      setDataVersion((v) => v + 1);
    } catch (err) {
      console.error('Erro ao excluir notificação:', err);
      toast.error(intl.formatMessage({ id: 'notifications.deleteError', defaultMessage: 'Erro ao excluir.' }));
    }
  };

  const markAllAsRead = async () => {
    if (!user?.uid) return;
    try {
      const col = collection(db, `users/${user.uid}/notifications`);
      const qUnread = query(col, where('read', '==', false), limit(300));
      const snap = await getDocs(qUnread);
      if (snap.empty) {
        toast.info(intl.formatMessage({ id: 'notifications.noneToMark', defaultMessage: 'Nenhuma notificação para marcar como lida.' }));
        return;
      }
      const batch = writeBatch(db);
      snap.docs.forEach((d) => {
        batch.update(d.ref, { read: true, updatedAt: serverTimestamp() });
      });
      await batch.commit();
      toast.success(
        intl.formatMessage(
          { id: 'notifications.allRead', defaultMessage: '{count} notificações marcadas como lidas.' },
          { count: snap.docs.length }
        )
      );
      setDataVersion((v) => v + 1);
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
      toast.error(intl.formatMessage({ id: 'notifications.markAllError', defaultMessage: 'Erro ao marcar todas como lidas.' }));
    }
  };

  const openLink = (n) => {
    if (n?.link) {
      try {
        window.open(n.link, '_blank', 'noopener,noreferrer');
      } catch {}
    }
  };

  return (
    <div className="la">
      <div className="dashboard-content">
        <h1 className="page-title">
          {intl.formatMessage({ id: 'notifications.title', defaultMessage: 'Notificações' })}
        </h1>
        <p className="subtitle-muted">
          {intl.formatMessage({
            id: 'notifications.subtitle',
            defaultMessage: 'Acompanhe avisos do sistema, alertas e comunicações importantes.',
          })}
        </p>

        {/* Summary */}
        <section className="card mb-16">
          <div className="card-body">
            <div className="grid-3">
              <div className="stat">
                <div className="stat-title">{intl.formatMessage({ id: 'notifications.total', defaultMessage: 'Total' })}</div>
                <div className="stat-value">{totalCount}</div>
              </div>
              <div className="stat">
                <div className="stat-title">{intl.formatMessage({ id: 'notifications.unread', defaultMessage: 'Não lidas' })}</div>
                <div className="stat-value">{unreadCount}</div>
              </div>
              <div className="stat actions">
                <button className="secondary-button" onClick={markAllAsRead} disabled={unreadCount === 0}>
                  {intl.formatMessage({ id: 'notifications.markAllAsRead', defaultMessage: 'Marcar todas como lidas' })}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="card mb-16">
          <div className="card-header">
            <div className="section-title">
              {intl.formatMessage({ id: 'notifications.filters', defaultMessage: 'Filtros' })}
            </div>
          </div>

          <div className="card-body grid-4">
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'notifications.status', defaultMessage: 'Status' })}</label>
              <select
                className="input-field"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">{intl.formatMessage({ id: 'notifications.allStatuses', defaultMessage: 'Todos' })}</option>
                <option value="unread">{intl.formatMessage({ id: 'notifications.unread', defaultMessage: 'Não lidas' })}</option>
                <option value="read">{intl.formatMessage({ id: 'notifications.read', defaultMessage: 'Lidas' })}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'notifications.type', defaultMessage: 'Tipo' })}</label>
              <select
                className="input-field"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">{intl.formatMessage({ id: 'notifications.allTypes', defaultMessage: 'Todos' })}</option>
                <option value="system">{intl.formatMessage({ id: 'notifications.type.system', defaultMessage: 'Sistema' })}</option>
                <option value="alert">{intl.formatMessage({ id: 'notifications.type.alert', defaultMessage: 'Alerta' })}</option>
                <option value="warning">{intl.formatMessage({ id: 'notifications.type.warning', defaultMessage: 'Aviso' })}</option>
                <option value="info">{intl.formatMessage({ id: 'notifications.type.info', defaultMessage: 'Informação' })}</option>
                {typesFromData
                  .filter((t) => !['system', 'alert', 'warning', 'info'].includes(String(t)))
                  .map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'notifications.from', defaultMessage: 'De' })}</label>
              <input
                type="date"
                className="input-field"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'notifications.to', defaultMessage: 'Até' })}</label>
              <input
                type="date"
                className="input-field"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>{intl.formatMessage({ id: 'notifications.search', defaultMessage: 'Busca' })}</label>
              <input
                className="input-field"
                placeholder={intl.formatMessage({
                  id: 'notifications.searchPlaceholder',
                  defaultMessage: 'Buscar por título, conteúdo…',
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* List */}
        <section className="card">
          <div className="card-header">
            <div className="section-title">
              {intl.formatMessage({ id: 'notifications.list', defaultMessage: 'Lista de Notificações' })}
            </div>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="loading-inline">
                {intl.formatMessage({ id: 'loading.label', defaultMessage: 'Carregando…' })}
              </div>
            ) : error ? (
              <div className="status-box danger">{error}</div>
            ) : items.length === 0 ? (
              <div className="status-box">
                {intl.formatMessage({ id: 'notifications.empty', defaultMessage: 'Nenhuma notificação encontrada.' })}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="la-table">
                  <thead>
                    <tr>
                      <th>{intl.formatMessage({ id: 'notifications.table.title', defaultMessage: 'Título' })}</th>
                      <th>{intl.formatMessage({ id: 'notifications.table.type', defaultMessage: 'Tipo' })}</th>
                      <th>{intl.formatMessage({ id: 'notifications.table.status', defaultMessage: 'Status' })}</th>
                      <th>{intl.formatMessage({ id: 'notifications.table.createdAt', defaultMessage: 'Criado em' })}</th>
                      <th>{intl.formatMessage({ id: 'user.actions', defaultMessage: 'Ações' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((n) => {
                      const createdAt =
                        n?.createdAt?.toDate?.() instanceof Date
                          ? n.createdAt.toDate()
                          : n?.createdAt instanceof Date
                          ? n.createdAt
                          : null;

                      return (
                        <tr key={n.id} className={!n.read ? 'is-unread' : undefined}>
                          <td>
                            <div className="col">
                              <div className="row gap-8 align-center">
                                {!n.read && (
                                  <span
                                    className="dot dot-primary"
                                    title={intl.formatMessage({ id: 'notifications.unread', defaultMessage: 'Não lida' })}
                                  />
                                )}
                                <strong>{n.title || '—'}</strong>
                              </div>
                              {n.body && <div className="muted small break-anywhere">{n.body}</div>}
                              {n.link && (
                                <button type="button" className="link-button small" onClick={() => openLink(n)}>
                                  {intl.formatMessage({ id: 'notifications.open', defaultMessage: 'Abrir' })}
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`chip chip-${n.type || 'default'}`}>{n.type || '—'}</span>
                          </td>
                          <td>
                            <span className={`chip ${n.read ? 'chip-success' : 'chip-warning'}`}>
                              {n.read
                                ? intl.formatMessage({ id: 'notifications.read', defaultMessage: 'Lida' })
                                : intl.formatMessage({ id: 'notifications.unread', defaultMessage: 'Não lida' })}
                            </span>
                          </td>
                          <td>{createdAt ? createdAt.toLocaleString() : '—'}</td>
                          <td>
                            <div className="row gap-8">
                              {n.read ? (
                                <button
                                  type="button"
                                  className="secondary-button"
                                  onClick={() => markRead(n.id, false)}
                                >
                                  {intl.formatMessage({ id: 'notifications.markUnread', defaultMessage: 'Marcar como não lida' })}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="secondary-button"
                                  onClick={() => markRead(n.id, true)}
                                >
                                  {intl.formatMessage({ id: 'notifications.markRead', defaultMessage: 'Marcar como lida' })}
                                </button>
                              )}

                              <button type="button" className="danger-button" onClick={() => deleteNotification(n.id)}>
                                {intl.formatMessage({ id: 'general.delete', defaultMessage: 'Excluir' })}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {!debouncedSearch && (
                  <div className="card-footer actions">
                    <button
                      className="secondary-button"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1 || loading}
                    >
                      &larr; {intl.formatMessage({ id: 'pagination.previous', defaultMessage: 'Anterior' })}
                    </button>
                    <span className="muted">
                      {intl.formatMessage({ id: 'pagination.page', defaultMessage: 'Página' })} {currentPage}
                    </span>
                    <button
                      className="secondary-button"
                      onClick={handleNextPage}
                      disabled={isLastPage || loading}
                    >
                      {intl.formatMessage({ id: 'pagination.next', defaultMessage: 'Próxima' })} &rarr;
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
